import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DollarSign, PiggyBank, Landmark, Wallet, ArrowRightLeft, Hotel, ChevronLeft, ChevronRight, Loader2, TrendingUp, FileText, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';

function PoolCard({ label, amount, pct, icon: Icon, color, purpose }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="w-4.5 h-4.5 text-white" />
        </div>
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
          <p className="text-lg font-bold text-white mono">${amount.toLocaleString()}</p>
        </div>
        <Badge className="ml-auto text-[10px] bg-white/5 text-gray-400">{pct}%</Badge>
      </div>
      <p className="text-[10px] text-gray-600">{purpose}</p>
    </div>
  );
}

export default function PlatformFeeLedgerTab() {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [page, setPage] = useState(1);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/platform-fees/summary`);
      const data = await res.json();
      setSummary(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchLedger = useCallback(async (p = 1) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/platform-fees/ledger?page=${p}&limit=20`);
      const data = await res.json();
      setLedger(data);
    } catch { /* ignore */ }
  }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/platform-fees/referral-ledger`);
      const data = await res.json();
      setReferrals(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { if (activeView === 'ledger') fetchLedger(page); }, [activeView, page, fetchLedger]);
  useEffect(() => { if (activeView === 'referrals') fetchReferrals(); }, [activeView, fetchReferrals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!summary) {
    return <p className="text-sm text-gray-500 text-center py-12">Failed to load fee data.</p>;
  }

  const { totals, pools, fee_config, hotel_fees, monthly_trend } = summary;

  return (
    <div className="space-y-6" data-testid="platform-fee-ledger-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold heading-font text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#F7931A]" />
            Platform Fee Ledger
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            6% commission split — protocol rewards, referrals, treasury, operations
          </p>
        </div>
        <div className="flex gap-1">
          {['overview', 'ledger', 'referrals'].map((v) => (
            <Button
              key={v}
              size="sm"
              variant={activeView === v ? 'default' : 'ghost'}
              onClick={() => setActiveView(v)}
              className={cn("text-xs capitalize", activeView !== v && "text-gray-400 hover:text-gray-200")}
              data-testid={`fee-view-${v}-btn`}
            >
              {v === 'overview' ? 'Overview' : v === 'ledger' ? 'Tax Ledger' : 'Referrals'}
            </Button>
          ))}
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Gross Revenue</p>
          <p className="text-xl font-bold text-white mono">${summary.gross_revenue.toLocaleString()}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Fees (6%)</p>
          <p className="text-xl font-bold text-[#F7931A] mono">${totals.total_fee.toLocaleString()}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Net to Hotels</p>
          <p className="text-xl font-bold text-emerald-400 mono">${summary.net_to_hotels.toLocaleString()}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Bookings</p>
          <p className="text-xl font-bold text-white mono">{summary.total_bookings}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Hotels</p>
          <p className="text-xl font-bold text-white mono">{summary.total_hotels}</p>
        </div>
      </div>

      {/* Overview View */}
      {activeView === 'overview' && (
        <>
          {/* Pool Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <PoolCard label="Protocol Allocation" amount={pools.protocol_allocation?.balance || 0} pct={fee_config.protocol_allocation_pct} icon={PiggyBank} color="bg-emerald-600/20" purpose={pools.protocol_allocation?.purpose || ''} />
            <PoolCard label="Treasury" amount={pools.treasury?.balance || 0} pct={fee_config.treasury_pct} icon={Landmark} color="bg-[#F7931A]/20" purpose={pools.treasury?.purpose || ''} />
            <PoolCard label="getbitsy Revenue" amount={pools.getbitsy_revenue?.balance || 0} pct={fee_config.getbitsy_revenue_pct} icon={Wallet} color="bg-purple-600/20" purpose={pools.getbitsy_revenue?.purpose || ''} />
            <PoolCard label="Operations" amount={pools.operations?.balance || 0} pct={fee_config.operations_pct} icon={DollarSign} color="bg-blue-600/20" purpose={pools.operations?.purpose || ''} />
          </div>

          {/* Monthly Trend */}
          {monthly_trend?.length > 0 && (
            <Card className="bg-white/[0.02] border-white/5">
              <CardContent className="p-5">
                <h3 className="font-semibold text-sm heading-font text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#F7931A]" />
                  Monthly Fee Collection
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-white/5 text-gray-500">
                        <th className="pb-2 text-left">Month</th>
                        <th className="pb-2 text-right">Gross</th>
                        <th className="pb-2 text-right">Total Fee</th>
                        <th className="pb-2 text-right">Protocol</th>
                        <th className="pb-2 text-right">Treasury</th>
                        <th className="pb-2 text-right">getbitsy Rev</th>
                        <th className="pb-2 text-right">Operations</th>
                        <th className="pb-2 text-right">Bookings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly_trend.map((m) => (
                        <tr key={m.month} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                          <td className="py-2 text-white font-medium">{m.month}</td>
                          <td className="py-2 text-right mono text-gray-300">${m.gross.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-[#F7931A]">${m.fees.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-emerald-400">${m.protocol_allocation.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-amber-400">${m.treasury.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-purple-400">${m.getbitsy_revenue.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-purple-400">${m.operations.toLocaleString()}</td>
                          <td className="py-2 text-right mono text-gray-400">{m.bookings}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hotel Fee Table */}
          <Card className="bg-white/[0.02] border-white/5">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm heading-font text-white mb-3 flex items-center gap-2">
                <Hotel className="w-4 h-4 text-[#F7931A]" />
                Fees by Hotel (Top 20)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="hotel-fees-table">
                  <thead>
                    <tr className="border-b border-white/5 text-gray-500">
                      <th className="pb-2 text-left">Hotel</th>
                      <th className="pb-2 text-left">Location</th>
                      <th className="pb-2 text-right">Bookings</th>
                      <th className="pb-2 text-right">Gross Revenue</th>
                      <th className="pb-2 text-right">Platform Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotel_fees.map((h) => (
                      <tr key={h.hotel_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                        <td className="py-2 text-white font-medium">{h.hotel_name}</td>
                        <td className="py-2 text-gray-400">{h.city}</td>
                        <td className="py-2 text-right mono text-gray-300">{h.bookings}</td>
                        <td className="py-2 text-right mono text-gray-300">${h.gross_revenue.toLocaleString()}</td>
                        <td className="py-2 text-right mono text-[#F7931A]">${h.platform_fees.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Tax Ledger View */}
      {activeView === 'ledger' && ledger && (
        <Card className="bg-white/[0.02] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm heading-font text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#F7931A]" />
                Tax Ledger — Per-Booking Fee Split
              </h3>
              <p className="text-[10px] text-gray-500">
                Page {ledger.pagination.page} of {ledger.pagination.pages} ({ledger.pagination.total} entries)
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="tax-ledger-table">
                <thead>
                  <tr className="border-b border-white/5 text-gray-500">
                    <th className="pb-2 text-left">Date</th>
                    <th className="pb-2 text-left">Hotel</th>
                    <th className="pb-2 text-left">Guest</th>
                    <th className="pb-2 text-left">Source</th>
                    <th className="pb-2 text-right">Gross</th>
                    <th className="pb-2 text-right">Protocol 1%</th>
                    <th className="pb-2 text-right">Treasury 1%</th>
                    <th className="pb-2 text-right">getbitsy 1%</th>
                    <th className="pb-2 text-right">Ops 3%</th>
                    <th className="pb-2 text-right">Total Fee</th>
                    <th className="pb-2 text-right">Net to Hotel</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((e) => (
                    <tr key={e.booking_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="py-2 mono text-gray-400">{e.created_at?.slice(0, 10)}</td>
                      <td className="py-2 text-white text-[11px]">{e.hotel_name}</td>
                      <td className="py-2 text-gray-300 text-[11px]">{e.guest_name}</td>
                      <td className="py-2">
                        {e.source === 'channex' ? (
                          <Badge className="text-[9px] bg-amber-500/10 text-amber-400">{e.ota_name || 'OTA'}</Badge>
                        ) : e.payment_method === 'crypto' ? (
                          <Badge className="text-[9px] bg-emerald-500/10 text-emerald-400">Crypto</Badge>
                        ) : (
                          <Badge className="text-[9px] bg-white/5 text-gray-400">Direct</Badge>
                        )}
                      </td>
                      <td className="py-2 text-right mono text-gray-300">${e.gross_revenue}</td>
                      <td className="py-2 text-right mono text-emerald-400">${e.fee_split.protocol_allocation}</td>
                      <td className="py-2 text-right mono text-amber-400">${e.fee_split.treasury}</td>
                      <td className="py-2 text-right mono text-purple-400">${e.fee_split.getbitsy_revenue}</td>
                      <td className="py-2 text-right mono text-purple-400">${e.fee_split.operations}</td>
                      <td className="py-2 text-right mono text-[#F7931A] font-semibold">${e.fee_split.total_fee}</td>
                      <td className="py-2 text-right mono text-white font-semibold">${e.net_to_hotel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-gray-400">
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-xs text-gray-500">Page {page}</span>
              <Button size="sm" variant="ghost" disabled={page >= ledger.pagination.pages} onClick={() => setPage(p => p + 1)} className="text-gray-400">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referrals View */}
      {activeView === 'referrals' && (
        <Card className="bg-white/[0.02] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm heading-font text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Referral Ledger
              </h3>
              {referrals && (
                <div className="flex gap-3 text-[10px]">
                  <span className="text-emerald-400">Paid: ${referrals.total_paid}</span>
                  <span className="text-amber-400">Pending: ${referrals.total_pending}</span>
                </div>
              )}
            </div>

            {(!referrals || referrals.entries.length === 0) ? (
              <div className="text-center py-10">
                <Users className="w-8 h-8 mx-auto mb-2 text-gray-700" />
                <p className="text-sm text-gray-500">No referrals yet.</p>
                <p className="text-xs text-gray-600 mt-1">When Hotel A refers Hotel B, earnings from the 1% referral pool will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="referral-ledger-table">
                  <thead>
                    <tr className="border-b border-white/5 text-gray-500">
                      <th className="pb-2 text-left">Referrer</th>
                      <th className="pb-2 text-left">Referred Hotel</th>
                      <th className="pb-2 text-right">Referred Revenue</th>
                      <th className="pb-2 text-right">Referral Earnings</th>
                      <th className="pb-2 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.entries.map((r, i) => (
                      <tr key={i} className="border-b border-white/[0.03]">
                        <td className="py-2 text-white">{r.referrer_name}</td>
                        <td className="py-2 text-gray-300">{r.referred_name}</td>
                        <td className="py-2 text-right mono text-gray-300">${r.referred_revenue.toLocaleString()}</td>
                        <td className="py-2 text-right mono text-blue-400">${r.referral_earnings.toLocaleString()}</td>
                        <td className="py-2 text-right">
                          <Badge className={cn("text-[9px]", r.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400')}>
                            {r.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
